import React, { useState, useEffect, useCallback } from 'react'; 
import {
    getVendorProducts,
    createVendorProduct,
    updateVendorProduct,
    updateProductPricing,
    deleteVendorProduct,
    setProductFeatured,
    getApiErrorMessage,
} from '../../services/vendorService';
 
import { VendorProduct, ProductCategory } from '../../types/vendor';
 
import DeleteConfirmationModal from '../DeleteConfirmationModal/DeleteConfirmationModal';
import AddProductModal from '../AddProductModal/AddProductModal';
import EditProductModal from '../EditProductModal/EditProductModal';
import UpdatePricingModal from '../UpdatePricingModal/UpdatePricingModal';
 
import { FiStar, FiTag, FiEdit2, FiTrash2 } from 'react-icons/fi';
import emptyStateIcon from '../../assets/images/empty.png';
 
import './VendorProductsTab.css';
 
interface VendorProductsTabProps {
    vendorId: string;
}
  
type ProductUpdateData = Partial<Pick<VendorProduct, 'name' | 'description' | 'quantity' | 'productStatus'>>;
type PricingUpdateData = {
    priceNGN?: number;
    priceGBP?: number;
};
 
const formatCategory = (category: ProductCategory | string) => {
    if (!category) return 'N/A';
    return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
};

const VendorProductsTab: React.FC<VendorProductsTabProps> = ({ vendorId }) => { 
    const [products, setProducts] = useState<VendorProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);  
     
    const [modal, setModal] = useState<'add' | 'edit' | 'pricing' | 'delete' | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<VendorProduct | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);  
 
    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getVendorProducts(vendorId); 
            setProducts(response.results || []); 
        } catch (err) {
            setError("Failed to load vendor's products.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [vendorId, refreshTrigger]);
 
    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);
  

    const handleAddProduct = async (formData: FormData) => {
        setIsProcessing(true);
        try {
            await createVendorProduct(vendorId, formData);
            closeModal();
            setRefreshTrigger(p => p + 1);  
        } catch (error) {
            console.error("Create Product Error:", error);
            alert(getApiErrorMessage(error, "Failed to create product."));
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpdateProduct = async (updates: ProductUpdateData) => {
        if (!selectedProduct) return;
        setIsProcessing(true);
        try {
            await updateVendorProduct(selectedProduct.id, updates);
            closeModal();
            setRefreshTrigger(p => p + 1);
        } catch (err) {
            alert('Failed to update product details.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpdatePricing = async (updates: PricingUpdateData) => {
        if (!selectedProduct) return;
        setIsProcessing(true);
        try {
            await updateProductPricing(selectedProduct.id, updates);
            closeModal();
            setRefreshTrigger(p => p + 1);
        } catch (err) {
            alert('Failed to update product pricing.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDeleteProduct = async () => {
        if (!selectedProduct) return;
        setIsProcessing(true);
        try {
            await deleteVendorProduct(selectedProduct.id);
            closeModal();
            setRefreshTrigger(p => p + 1);
        } catch (err) {
            alert('Failed to delete product.');
        } finally {
            setIsProcessing(false);
        }
    };
 
    const handleToggleFeatured = async (product: VendorProduct) => {
        const action = product.isFeatured ? 'un-feature' : 'feature';
        if (!window.confirm(`Are you sure you want to ${action} this product?`)) {
            return;
        }

        setIsProcessing(true);
        try {
            await setProductFeatured(product.id, !product.isFeatured);
            setRefreshTrigger(p => p + 1);
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || `Failed to ${action} the product.`;
            alert(`Error: ${errorMessage}`);
        } finally {
            setIsProcessing(false);
        }
    };
 
    const closeModal = () => {
        setModal(null);
        setSelectedProduct(null);
    };
 
    if (loading) return <div className="tab-loading">Loading products...</div>;
    if (error) return <div className="tab-error">{error}</div>;
 
    return (
        <>
            <div className="vendor-products-tab">
                <div className="tab-header">
                    <h4>Products ({products.length})</h4>
                    <button className="add-product-btn" onClick={() => setModal('add')}>
                        Add New Product
                    </button>
                </div>

                {products.length === 0 ? (
                    <div className="tab--empty">
                        <img src={emptyStateIcon} alt="No products" />
                        <p>This vendor has not added any products yet.</p>
                    </div>
                ) : (
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Category</th>
                                    <th>Price (NGN)</th>
                                    <th>Price (GBP)</th>
                                    <th>Quantity</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product) => (
                                    <tr key={product.id}>
                                        <td data-label="Name">{product.name}</td>
                                        <td data-label="Category">{formatCategory(product.category)}</td>
                                         
                                        <td data-label="Price (NGN)">
                                            {product.priceNGN > 0 
                                                ? `₦${Number(product.priceNGN).toLocaleString()}` 
                                                : (product.pricing?.NGN ? `₦${Number(product.pricing.NGN).toLocaleString()}` : '-')
                                            }
                                        </td>
 
                                        <td data-label="Price (GBP)">
                                            {product.priceGBP > 0 
                                                ? `£${Number(product.priceGBP).toLocaleString()}` 
                                                : (product.pricing?.GBP ? `£${Number(product.pricing.GBP).toLocaleString()}` : '-')
                                            }
                                        </td>

                                        <td data-label="Quantity">{product.quantity}</td>
                                        <td data-label="Status">
                                            <span className={`status-badge status-${product.productStatus?.toLowerCase() || 'active'}`}>
                                                {product.productStatus?.replace(/_/g, ' ') || 'Active'}
                                            </span>
                                        </td>
                                        <td data-label="Actions">
                                            <div className="table-action-buttons">
                                                <button
                                                    type="button"
                                                    className={`table-action-btn ${product.isFeatured ? 'featured' : ''}`}
                                                    title={product.isFeatured ? 'Un-feature Product' : 'Feature Product'}
                                                    aria-label="Toggle featured"
                                                    onClick={() => handleToggleFeatured(product)}
                                                >
                                                    <FiStar />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="table-action-btn"
                                                    title="Update Pricing"
                                                    aria-label="Update pricing"
                                                    onClick={() => { setSelectedProduct(product); setModal('pricing'); }}
                                                >
                                                    <FiTag />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="table-action-btn"
                                                    title="Edit Details"
                                                    aria-label="Edit product"
                                                    onClick={() => { setSelectedProduct(product); setModal('edit'); }}
                                                >
                                                    <FiEdit2 />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="table-action-btn danger"
                                                    title="Delete Product"
                                                    aria-label="Delete product"
                                                    onClick={() => { setSelectedProduct(product); setModal('delete'); }}
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div> 
            
            {modal === 'add' && (
                <AddProductModal onClose={closeModal} onSave={handleAddProduct} isSaving={isProcessing} />
            )}

            {modal === 'edit' && selectedProduct && (
                <EditProductModal product={selectedProduct} onClose={closeModal} onSave={handleUpdateProduct} isSaving={isProcessing} />
            )}

            {modal === 'pricing' && selectedProduct && (
                <UpdatePricingModal product={selectedProduct} onClose={closeModal} onSave={handleUpdatePricing} isSaving={isProcessing} />
            )}

            {modal === 'delete' && selectedProduct && (
                <DeleteConfirmationModal onClose={closeModal} onConfirm={handleDeleteProduct} isDeleting={isProcessing} itemName={selectedProduct.name} />
            )}
        </>
    );
};

export default VendorProductsTab;